// Trigger sets — authored per report; see docs/builder-report-authoring-spec.md.
// Batch "coshhChemicals": solvents-paints, lead-paint.

import type { TriggerQuestion } from "./types";
import {
  ASBESTOS,
  OCCUPIED,
  WAH,
  ACCESS_OPTIONS,
  HOT_WORK_PERMIT_Q,
  COSHH_DETAIL_QS,
  RESCUE_Q,
  PRE2000_Q,
} from "./shared";

// Report-local hazard ids (not in the shared canonical set).
const SOLVENT_VAPOUR = "haz-coshh-solvent-vapour-inhalation-in-enclosed-space";
const ISOCYANATE = "haz-coshh-isocyanate-exposure-from-two-pack-coating";
const SOLVENT_SKIN = "haz-coshh-skin-contact-and-dermatiti-from-solvent";
const SOLVENT_RAGS = "haz-coshh-solvent-soaked-rag-self-heating-in-waste";
const LEAD_TAKE_HOME = "haz-coshh-lead-take-home-contamination-of-familie";
const LEAD_SURVEILLANCE = "haz-coshh-lead-failure-to-arrange-blood-lead-surveillance";
const LEAD_HIGH_EXPOSURE = "haz-coshh-lead-high-exposure-method-without-correct-control";
const FIRE = "haz-hot-works-fire-from-hot-work-and-ignition-of-combustible";

export const COSHH_CHEMICALS_TRIGGERS: Record<string, TriggerQuestion[]> = {
  "solvents-paints": [
    ...COSHH_DETAIL_QS,
    {
      key: "twoPack",
      label: "Will two-pack (2K) or isocyanate-containing coatings be used?",
      kind: "yesno",
      effects: {
        yes: {
          hazardsOn: [ISOCYANATE],
          scopeLine: "Two-pack/isocyanate coatings in use — mixed to the SDS ratio with mechanical extraction, air-fed or appropriate RPE, and exposed workers under COSHH Regulation 11 health surveillance.",
        },
        no: {
          hazardsOff: [ISOCYANATE],
          emergencyOff: ["Asthma or breathing symptoms from isocyanate"],
          substancesOff: ["Two-pack"],
          scopeLine: "No two-pack or isocyanate-containing coatings — only single-pack solvent or water-based products are used.",
        },
      },
      notSure: {
        hazardsOn: [ISOCYANATE],
        failCheck: "Two-pack / isocyanate content confirmed against the SDS",
      },
    },
    {
      key: "flammable",
      label: "Are the products flammable (solvent-based, carrying a flammable label)?",
      kind: "yesno",
      effects: {
        yes: {
          hazardsOn: [FIRE, SOLVENT_VAPOUR, SOLVENT_RAGS],
          scopeLine: "Flammable solvent products in use — DSEAR ignition control applies: no hot works, naked flame or sparking tools in the area, day quantities only at the work face and bulk in a fire-rated store.",
        },
        no: {
          hazardsOff: [FIRE, SOLVENT_RAGS],
          permitsRemove: ["Hot work permit"],
          emergencyOff: ["Fire or explosive atmosphere"],
          substancesOff: ["Solvent-based paints"],
          scopeLine: "Water-based / non-flammable products only — DSEAR flammable-storage and ignition controls are not required, though vapour and skin controls still apply.",
        },
      },
      notSure: {
        hazardsOn: [FIRE, SOLVENT_VAPOUR, SOLVENT_RAGS],
        failCheck: "Flammability confirmed against the SDS",
      },
    },
    {
      key: "hotWorksNearby",
      label: "Could hot works, naked flame or sparking tools be present in or near the area while solvents are open?",
      kind: "yesno",
      effects: {
        yes: {
          permitsAdd: ["Hot work permit"],
          scopeLine: "Hot works possible nearby — a hot work permit is required, ignition sources are excluded while solvents are open and vapour persists, and a fire watch is maintained.",
        },
        no: {
          permitsRemove: ["Hot work permit"],
          scopeLine: "No hot works, naked flame or sparking tools in or near the work area while solvents are open.",
        },
      },
      notSure: {
        permitsAdd: ["Hot work permit"],
        failCheck: "Hot works / ignition sources in the area confirmed",
      },
      followUps: { when: "yes", questions: [HOT_WORK_PERMIT_Q] },
    },
    {
      key: "ventilation",
      label: "How will the work area be ventilated?",
      kind: "choice",
      options: ["Natural ventilation (open doors/windows) — brush/roller, well-ventilated", "Mechanical extraction (enclosed or poorly ventilated space)", "Air-fed RPE with extraction (spraying)"],
      effects: {
        "Natural ventilation (open doors/windows) — brush/roller, well-ventilated": { scopeLine: "Ventilation: natural (doors/windows open) — suitable for brush and roller work in a well-ventilated area." },
        "Mechanical extraction (enclosed or poorly ventilated space)": { scopeLine: "Ventilation: mechanical extraction provided because natural airflow is poor or the space is enclosed." },
        "Air-fed RPE with extraction (spraying)": { scopeLine: "Ventilation: local exhaust extraction with air-fed RPE for spraying." },
      },
    },
    {
      key: "skinContact",
      label: "Is direct skin contact with solvents foreseeable (handling, decanting, cleaning)?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [SOLVENT_SKIN] },
        no: { hazardsOff: [SOLVENT_SKIN], scopeLine: "No foreseeable direct skin contact — product is applied by brush/roller with solvent-resistant gloves and washing facilities at the work face." },
      },
      notSure: { hazardsOn: [SOLVENT_SKIN], failCheck: "Skin-contact exposure confirmed" },
    },
    {
      key: "occupied",
      label: "Is the property occupied during the work (fumes could reach occupants)?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [OCCUPIED], requireFields: ["nearbyTradesOrPublic"] },
        no: { hazardsOff: [OCCUPIED], sequenceOff: [6], scopeLine: "Premises unoccupied during the works — occupant-fume controls are not required for this task." },
      },
      notSure: { hazardsOn: [OCCUPIED], failCheck: "Occupancy during the works confirmed" },
    },
    {
      key: "height",
      label: "Is any painting or coating carried out at height?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [WAH] },
        no: { hazardsOff: [WAH], scopeLine: "All work is reachable from the ground — work-at-height controls are not required for this task." },
      },
      notSure: { hazardsOn: [WAH], failCheck: "Work-at-height requirement confirmed" },
      followUps: {
        when: "yes",
        questions: [
          {
            key: "accessMethod",
            label: "What access equipment will be used?",
            kind: "choice",
            options: ACCESS_OPTIONS,
            effects: Object.fromEntries(ACCESS_OPTIONS.map((o) => [o, { scopeLine: `Access for work at height: ${o}.` }])),
          },
          RESCUE_Q,
        ],
      },
    },
  ],

  "lead-paint": [
    ...COSHH_DETAIL_QS,
    {
      key: "leadTested",
      label: "Has the paint been tested (or its age assessed) to confirm whether it contains lead?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "Lead content confirmed by testing or building age before work; the removal method and CLAW work category are set accordingly." },
        no: { failCheck: "Lead-in-paint testing / age assessment confirmed" },
      },
      notSure: { failCheck: "Lead-in-paint testing / age assessment confirmed" },
    },
    PRE2000_Q([ASBESTOS], [ASBESTOS]),
    {
      key: "removalMethod",
      label: "How will the lead paint be prepared or removed?",
      kind: "choice",
      options: ["Leave in place — key lightly with wet abrasive", "Wet stripping", "Chemical stripping", "Controlled scraping with H/M-class extraction", "Low-temperature heat (under 500C)"],
      effects: {
        "Leave in place — key lightly with wet abrasive": { hazardsOff: [LEAD_HIGH_EXPOSURE], scopeLine: "Sound leaded paint is left in place and keyed lightly with wet abrasive — no high-exposure removal method is used." },
        "Wet stripping": { scopeLine: "Removal method: wet stripping to keep lead bound in liquid rather than airborne." },
        "Chemical stripping": { scopeLine: "Removal method: chemical stripping to keep lead bound in paste rather than airborne." },
        "Controlled scraping with H/M-class extraction": { scopeLine: "Removal method: controlled scraping with H/M-class on-tool extraction; CLAW work category determined for the method." },
        "Low-temperature heat (under 500C)": { scopeLine: "Removal method: low-temperature heat below 500 degrees C — no blow lamps or torches; lead fume controlled and the CLAW work category determined." },
      },
    },
    {
      key: "hygieneRegime",
      label: "Is the hygiene regime in place (no eating/drinking/smoking in the area, washing before breaks)?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "Hygiene regime in place: eating, drinking and smoking prohibited in the work area; hands and forearms washed before every break and disposable coveralls removed before leaving." },
        no: { failCheck: "Lead hygiene and decontamination regime confirmed" },
      },
      notSure: { failCheck: "Lead hygiene and decontamination regime confirmed" },
    },
    {
      key: "significantExposure",
      label: "Could exposure be significant (regular disturbance, dry methods, enclosed space)?",
      kind: "yesno",
      effects: {
        yes: {
          hazardsOn: [LEAD_SURVEILLANCE],
          scopeLine: "Exposure may be significant — affected workers enrolled in blood-lead medical surveillance under CLAW, with air monitoring and action/suspension levels applied.",
        },
        no: {
          hazardsOff: [LEAD_SURVEILLANCE],
          emergencyOff: ["Blood-lead at suspension level"],
          scopeLine: "Exposure is not significant for the chosen method, but hygiene, decontamination and dust controls still apply throughout.",
        },
      },
      notSure: {
        hazardsOn: [LEAD_SURVEILLANCE],
        failCheck: "Exposure significance and surveillance requirement confirmed",
      },
    },
    {
      key: "occupied",
      label: "Is the property occupied (occupants, children or pregnant women could be present)?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [LEAD_TAKE_HOME], requireFields: ["nearbyTradesOrPublic"], scopeLine: "Premises occupied — work area sheeted and segregated, children and pregnant women kept away entirely, and the area cleaned down before reoccupation." },
        no: { scopeLine: "Premises unoccupied during the works — no occupants, children or pregnant women in or near the work area." },
      },
      notSure: { hazardsOn: [LEAD_TAKE_HOME], failCheck: "Occupancy during the works confirmed" },
    },
    {
      key: "height",
      label: "Is any stripping or painting carried out at height?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [WAH] },
        no: { hazardsOff: [WAH], scopeLine: "All work is reachable from the ground — work-at-height controls are not required for this task." },
      },
      notSure: { hazardsOn: [WAH], failCheck: "Work-at-height requirement confirmed" },
      followUps: {
        when: "yes",
        questions: [
          {
            key: "accessMethod",
            label: "What access equipment will be used?",
            kind: "choice",
            options: ACCESS_OPTIONS,
            effects: Object.fromEntries(ACCESS_OPTIONS.map((o) => [o, { scopeLine: `Access for work at height: ${o}.` }])),
          },
          RESCUE_Q,
        ],
      },
    },
  ],
};
